import React, { useRef } from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  Variants,
} from 'framer-motion';

const viewport = { once: true, margin: '-60px' } as const;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  y?: number;
}

export const Reveal: React.FC<RevealProps> = ({ children, className, delay = 0, y = 24 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={viewport}
    transition={{ duration: 0.6, ease: 'easeOut', delay }}
  >
    {children}
  </motion.div>
);

interface StaggerContainerProps {
  children: React.ReactNode;
  className?: string;
  stagger?: number;
  delay?: number;
}

export const StaggerContainer: React.FC<StaggerContainerProps> = ({
  children,
  className,
  stagger = 0.08,
  delay = 0,
}) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={viewport}
    variants={{
      hidden: {},
      show: { transition: { staggerChildren: stagger, delayChildren: delay } },
    }}
  >
    {children}
  </motion.div>
);

interface StaggerItemProps {
  children: React.ReactNode;
  className?: string;
}

export const StaggerItem: React.FC<StaggerItemProps> = ({ children, className }) => (
  <motion.div className={className} variants={fadeUp}>
    {children}
  </motion.div>
);

interface ParallaxProps {
  children: React.ReactNode;
  className?: string;
  speed?: number;
}

// Drifts content counter to the page scroll while it travels through the
// viewport. MotionConfig's reducedMotion does not cover style bindings,
// so reduced motion is gated here explicitly.
export const Parallax: React.FC<ParallaxProps> = ({ children, className, speed = 0.3 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [speed * 100, -speed * 100]);

  return (
    <motion.div ref={ref} className={className} style={reducedMotion ? undefined : { y }}>
      {children}
    </motion.div>
  );
};
